import { Headers, Method, callFetch } from ".";
import { toErrorPayload } from "../../../helpers/errorPayload";
import { SERVER_ERROR_MESSAGE, API_HOST_ERROR_MESSAGE } from "../../../constants/errorMessages";
import { ApplicationState } from "../../nissan_no/index";
import { AddOnState, IAddOn, LowMillage, PrepareNorwayAddonTexts } from "../../AddOn";
import { Coverage, CoverageItem, CoveragePrice, Deductible, InsuranceState } from "../../Insurance";
import { ProfileState } from "../../Profile";
import { FooterState, GetUpdatedNoFooterPrices } from "../../Footer";
import { AvailableBonus, Bonus, Company } from "../../nissan_no/Bonus";
import { FinancingTypesState, IFinancingCompany, IFinancingType } from "../../nissan_no/FinancingType";
import { BrandsEnum } from "../../../enums";
import { DistanceNoState, DistanceOptionNo } from "../../nissan_no/DistanceNo";
import { ImportedUsedState } from "../../ImportedUsed";
import CarImageService from "../../../services/CarImageService";

type QuoteRequest = {
  brand: string;
  policyHolder: {
    socialSecurityNumber: string;
    firstName: string;
    lastName: string;
  };
  vehicle: {
    currentMileageBelow150000Kilometers: boolean;
    vehicleIdentity?: {
      registrationNumber?: string;
    };
    modelInformation?: {
      ifModelCode?: string;
      firstRegistrationYear?: number;
      importedUsed?: boolean;
    };
  };
  optionalPriceCalculationParameters?: {
    selectedMileageOptionId?: string;
    driverBelow23YearsOfAge?: boolean;
    financingType?: string;
  };
};

export const mapMileageOptions = (mileageOptions: DistanceOptionNo[]): DistanceOptionNo[] => {
  const mappedMileageOptions = mileageOptions.map(option => ({
    id: option.id,
    value: option.value,
    unit: option.unit,
    title: option.value + " km",
  }));

  // special case - for milgeage id with "Unlimited", change title to "Ubegrenset"
  mappedMileageOptions.map(option => {
    if (option.id === "Unlimited") {
      option.title = "Ubegrenset";
    }
    return option;
  });

  return mappedMileageOptions;
};

export const getQuotePayload = (state: ApplicationState) => {
  let data: QuoteRequest;
  const brand = state.inputs.partner;
  const policyHolder = {
    socialSecurityNumber: state.inputs.socialSecurityNumberNo ?? "",
    firstName: state.inputs.firstName ?? "",
    lastName: state.inputs.lastName ?? "",
  };
  const priceCalcParams = {
    selectedMileageOptionId: state.distance?.selectedId ?? "",
    driverBelow23YearsOfAge: state?.youngDriver?.selected ?? false,
  };

  if (state.inputs.isCarModel) {
    data = {
      brand: brand,
      policyHolder: policyHolder,
      vehicle: {
        currentMileageBelow150000Kilometers: true,
        modelInformation: {
          ifModelCode: state.inputs.vehicle?.modelIdentity?.modelIdentifier,
          firstRegistrationYear: state.inputs.vehicle?.modelIdentity?.firstRegistrationYear,
          importedUsed: state.importedUsed?.selected ?? false,
        },
      },
      optionalPriceCalculationParameters: priceCalcParams,
    };
  } else {
    data = {
      brand: brand,
      policyHolder: policyHolder,
      vehicle: {
        currentMileageBelow150000Kilometers: true,
        vehicleIdentity: {
          registrationNumber: state.inputs.vehicle?.vehicleIdentity?.registrationNumberNo,
        },
      },
      optionalPriceCalculationParameters: priceCalcParams,
    };
  }
  return JSON.stringify(data);
};

const quote = async (state: ApplicationState, dispatch?: Function): Promise<ApplicationState> => {
  const apiHost = state.inputs.apiHost;
  const brand = state.inputs.partner;
  if (!apiHost) {
    if (dispatch)
      dispatch({
        type: "SET_SERVER_ERROR",
        payload: toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE }),
      });
    throw toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE });
  }

  if (!state.inputs.partner) {
    throw Error("PARTNER ERROR");
  }

  const payload = getQuotePayload(state);
  const fetchBuilder = () =>
    fetch(`${apiHost}/api/no/${brand}/quote`, {
      method: Method,
      mode: "cors",
      headers: Headers,
      body: payload,
    });

  const carImageResponse = await CarImageService(apiHost, state.inputs.vehicle?.vehicleIdentity?.registrationNumberNo ?? "", brand);
  const response = await callFetch(fetchBuilder);
  const json = await response.json();

  if (response.status !== 200) {
    const message = (json && json.message) ?? SERVER_ERROR_MESSAGE;
    if (dispatch)
      dispatch({
        type: "SET_SERVER_ERROR",
        payload: toErrorPayload({ message, header: SERVER_ERROR_MESSAGE }),
      });
    throw toErrorPayload({ message, header: SERVER_ERROR_MESSAGE });
  }

  const coverages = json.coverages.map(
    (val: any) =>
      ({
        InternalCode: val.id,
        LongName: val.name,
        ShortName: val.name.substring(0, 3),
        Description: addCoverageDescriptionFromResponse(val, json.uiContent),
        PrimaryPrice: mapCoveragePrice(val.price),
        SecondaryPrice: {},
        IsRecommended: val.isSelected,
        CoverageItems: mapCoverageItemsInCoverage(json.coverageItems, val.includedCoverageItems),
        Deductibles: mapDeductibles(val.deductibles),
        CoverageAddons: mapAddonsFromResponse(val.addons, json.uiContent, brand),
        CalculatedPrice: preparePrice(val.price.find((x: any) => x.interval === "Monthly")),
        CalculatedPriceSecondary: "",
      } as Coverage),
  );

  const currentCoverage = coverages.find((x: any) => x.IsRecommended === true) as Coverage;
  const currentCoverageTax = json.vehicle.insuranceTax.find((x: any) => x.interval === "Monthly");

  const selectedAddonInternalCodes = currentCoverage.CoverageAddons?.filter((x: any) => x.Checked)
    ?.map((x: IAddOn) => x.InternalCode)
    ?.filter((x: any) => x != null) as string[];

  const footerPrices = GetUpdatedNoFooterPrices(state.footer as FooterState, currentCoverage, selectedAddonInternalCodes);

  return {
    ...state,
    youngDriver: {
      ...state.youngDriver,
      title: json.uiContent.YoungDriver?.Header,
      values: [
        {
          value: true,
          text: "Ja",
        },
        {
          value: false,
          text: "Nei",
        },
      ],
      description: {
        text: json.uiContent.YoungDriver?.Popup.Body,
        title: json.uiContent.YoungDriver?.Popup.Header,
      },

      selected: json.priceCalculationParameters?.driverBelow23YearsOfAge ?? false,
    },
    importedUsed: {
      ...state.importedUsed,
      title: json.uiContent.ImportedUsed?.Header,
      values: [
        {
          value: true,
          text: "Ja",
        },
        {
          value: false,
          text: "Nei",
        },
      ],
      description: {
        text: json.uiContent.ImportedUsed?.Popup.Body,
        title: json.uiContent.ImportedUsed?.Popup.Header,
      },
      selected: json.priceCalculationParameters?.importedUsed ?? state.importedUsed?.selected ?? false,
    } as ImportedUsedState,
    profile: {
      ...state.profile,
      email: {
        title: json.uiContent.Email?.Header,
        placeholderText: json.uiContent.Email?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent.EmailRequiredErrorMessage, json.uiContent.EmailIncorrectErrorMessage],
        value: "",
      },
      phone: {
        title: json.uiContent.PhoneNumber?.Header,
        placeholderText: json.uiContent.PhoneNumber?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent.PhoneRequiredErrorMessage, json.uiContent.PhoneIncorrectErrorMessage],
        value: "",
      },
      isValid: true,
      actualMilage: {
        title: json.uiContent.ActualMilageLabel,
        placeholderText: json.uiContent.ActualMilagePlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.uiContent.ActualMilageRequired, json.uiContent.ActualMilageIncorrect],
        value: "",
      },
      gdpr: {
        questionVisible: true,
        selected: false,
        text: json.uiContent.GDPRText,
        requiredErrorMessage: json.uiContent.GDPRRequired,
        showError: false,
        readMore: "Les mer.",
        readMoreUrl: json.uiContent.GDPRUrl,
      },
      carModel: `${json.vehicle.make} ${json.vehicle.model}`,
      carYear: json.vehicle.modelYear,
      carImageUrl: carImageResponse?.carImageUrl
    } as ProfileState,
    addon: {
      ...state.addon,
      title: json.uiContent.AddonsHeader ?? state.addon?.title,
      addOns: currentCoverage.CoverageAddons,
      selectedAddOnInternalCodes: selectedAddonInternalCodes,
    } as AddOnState,
    distance: {
      ...state.distance,
      title: undefined,
      label: {
        title: json.uiContent.Mileage?.Header,
      },
      help: {
        title: json.uiContent.Mileage?.Popup?.Header,
        description: json.uiContent.Mileage?.Popup?.Body,
      },
      values: mapMileageOptions(json?.mileage?.availableMileageOptions),
      selected: json?.mileage?.availableMileageOptions?.find((x: any) => x.value === state.distance?.selectedId)?.value,
    } as DistanceNoState,
    footer: {
      ...state.footer,
      ...footerPrices,
      totalPriceTooltip: json.uiContent.TotalPriceTooltipText,
      purchaseButton: json.uiContent.BuyButtonText,
      expanded: false,
    } as FooterState,
    texts: {
      ...state.texts,
      confirmationHeader: json.uiContent.ConfirmationHeader,
      confirmationBody: json.uiContent.ConfirmationBody,
      confirmationExtraInfo: json.uiContent.ConfirmationExtraInfo,
      confirmationExtraUrl1: {
        urlText: json.uiContent.ConfirmationExtraUrl1?.Text,
        link: json.uiContent.ConfirmationExtraUrl1?.Url,
      },
      confirmationExtraUrl2: {
        urlText: json.uiContent.ConfirmationExtraUrl2?.Text,
        link: json.uiContent.ConfirmationExtraUrl2?.Url,
      },
      welcomeHeaderPartOneText: (json.uiContent.WelcomeHeaderPartOneText ?? state.texts?.welcomeHeaderPartOneText)?.trim(),
      ssnHeader: json.uiContent.SSNHeader ?? state.texts?.ssnHeader,
      registrationNumberHeader: json.uiContent.RegistrationNumberHeader ?? state.texts?.registrationNumberHeader,
      carModelHeader: json.uiContent.CarModelHeader ?? state.texts?.carModelHeader,
      checkoutButtonText: json.uiContent.CheckoutButtonText,
      coverageHeader: json.uiContent.CoverageHeader ?? state.texts?.coverageHeader,
      changeChoiceText: json.uiContent.ChangeChoiceButtonText ?? state.texts?.changeChoiceText,
      billingSectionHeader: json.uiContent.BillingHeader ?? state.texts?.billingSectionHeader,
      trialPeriodMessage: json.uiContent.Withdrawal.Popup.Header,
      infoBlockText: json.uiContent.InfoBlockText,
      preliminaryPriceText: json.uiContent.PreliminaryPriceText,
      antiTheftMessage: json.uiContent.AntiTheftMessage,
      confirmationPromotion1: {
        body: json.uiContent.ConfirmationPromotion1?.Body,
        header: json.uiContent.ConfirmationPromotion1?.Header,
        link: {
          text: json.uiContent.ConfirmationPromotion1?.Link.Text,
          url: json.uiContent.ConfirmationPromotion1?.Link.Url,
        },
      },
      singleRecommendedButton: json.uiContent.SingleRecomendedCoverageButton,
      discountMessageBoxText: json.uiContent.DiscountMessageBoxText ?? "Vi har trukket fra rabatter pa",
      confirmationBottomSection: {
        body: json.uiContent.ConfirmationBottomSection?.Body,
        header: json.uiContent.ConfirmationBottomSection?.Header,
        link: {
          text: json.uiContent.ConfirmationBottomSection?.Link.Text,
          url: json.uiContent.ConfirmationBottomSection?.Link.Url,
        },
      },
      confirmationExistingCustomerHeader: json.uiContent.ConfirmationExistingCustomerHeader,
      confirmationWithInstructionsHeader: json.uiContent.ConfirmationWithInstructionsHeader,
      confirmationWithInstructionsBody: json.uiContent.ConfirmationWithInstructionsBody,
      confirmationManualInstructionsHeader: json.uiContent.ConfirmationManualInstructionsHeader,
      confirmationInsuranceStartDate: json.uiContent.ConfirmationInsuranceStartDate ?? "Gjelder fra",
      customisePageButton: json.uiContent.CoveragePageButtonText,
      actualMilageHeader: json.uiContent.ActualMilageHeader,
      bonusSectionHeader: json.uiContent.BonusSectionHeader,
      contactsSectionHeader: json.uiContent.ContactsSectionHeader,
      coverageDescriptions: {
        coverageDescriptionSuper: json.uiContent.CoverageDescriptionSuper,
        coverageDescriptionKasko: json.uiContent.CoverageDescriptionKasko,
        coverageDescriptionDelkasko: json.uiContent.CoverageDescriptionDelkasko,
        coverageDescriptionAnsvar: json.uiContent.CoverageDescriptionAnsvar,
      },
      addonDescriptionMotor: json.uiContent.AddonDescriptionMotor,
      addonDescriptionLeiebil: json.uiContent.AddonDescriptionLeiebil,
      addonDescriptionMaskinskade: json.uiContent.AddonDescriptionMaskinskade,
      addonDescriptionTotalCare: json.uiContent.AddonDescriptionTotalCare,
      addonMotorCheckboxDescription: json.uiContent.AddonMotorCheckboxDescription,
      addonCheckboxErrorMessage: json.uiContent.AddonCheckboxErrorMessage,
      confirmationLinks: {
        ...state.texts?.confirmationLinks,
        checkoutExtraUrl1: {
          //Terms
          ...state.texts?.confirmationLinks?.checkoutExtraUrl1,
          urlText: json.uiContent.CheckoutExtraUrl1?.Text,
          link: json.termsAndRelatedInformation[1]?.link,
        },
        checkoutExtraUrl2: {
          //InsuranceProductInformationDocument
          ...state.texts?.confirmationLinks?.checkoutExtraUrl2,
          urlText: json.uiContent.CheckoutExtraUrl2?.Text,
          link: json.termsAndRelatedInformation[0]?.link,
        },
      },
    }, // as Texts,
    insurance: {
      ...state.insurance,
      coverage: {
        ...state.insurance?.coverage,
        recommendedTitle: json.uiContent.RecommendedCoverageLabel,
        ui: {
          deductible: {
            label: json.uiContent.Deductible.Header,
            helpHeader: json.uiContent.Deductible.Popup.Header,
            helpText: json.uiContent.Deductible.Popup.Body,
          },
          coverageSelect: {
            label: json.uiContent.SelectCoverageHeader ?? "Välj försäkring",
          },
        },
        selectedCode: currentCoverage.InternalCode,
        selectedName: currentCoverage.LongName,
        Coverages: coverages,
        CoverageItems: mapCoverageItems(json.coverageItems, coverages),
        vehicleInsuranceTax: {
          ...state.insurance?.coverage?.vehicleInsuranceTax,
          text: json.uiContent.InsuranceTaxText,
          readMoreText: "Las mere.",
          url: json.uiContent.InsuranceTaxUrl,
          amount: currentCoverageTax.amount,
          type: currentCoverageTax.type,
          currency: currentCoverageTax.currency,
        },
      },
    } as InsuranceState,
    financingOptions: {
      ...state.financingOptions,
      sectionHeader: json.uiContent.FinancingSectionHeader,
      financeTypeRequiredErrorMessage: json.uiContent.FinanceTypeRequiredErrorMessage,
      financeCompanyRequiredErrorMessage: json.uiContent.FinanceCompanyRequiredErrorMessage,
      financingTypes: [
        {
          //leasing
          ...state.financingOptions?.financingTypes?.[0],
          name: "Leasing",
          id: "0",
          selected: false,
          texts: {
            ...state.financingOptions?.financingTypes?.[0].texts,
            header: json.uiContent.LeasingHeader,
            description: json.uiContent.LeasingDescription,
          },
          companies: mapLeasingCompanies(json.leasingCompanies),
        } as IFinancingType,
        {
          //loan
          ...state.financingOptions?.financingTypes?.[1],
          id: "1",
          name: "Loan",
          selected: false,
          texts: {
            ...state.financingOptions?.financingTypes?.[1].texts,
            header: json.uiContent.LoanHeader,
            description: json.uiContent.LoanDescription,
          },
          companies: mapFinancingCompanies(json.financingCompanies),
        } as IFinancingType,
        {
          // no financing
          ...state.financingOptions?.financingTypes?.[2],
          id: "2",
          name: "Other",
          selected: false,
          texts: {
            ...state.financingOptions?.financingTypes?.[2].texts,
            header: json.uiContent.NoFinancingHeader,
          },
        } as IFinancingType,
      ],
    } as FinancingTypesState,
    bonus: {
      ...state.bonus,
      bonus: {
        ...state.bonus?.bonus,
        help: {
          title: json.uiContent.Bonus.Popup.Header,
          description: json.uiContent.Bonus.Popup.Body,
        },
        label: {
          title: json.uiContent.Bonus.Header,
        },
        bottomText: json.uiContent.BonusBottomText,
        requiredErrorMessage: json.uiContent.BonusErrorMessage,
        selectedId: json.priceCalculationParameters?.bonus?.selectedBonus?.id ?? "",
        availableBonuses: json.priceCalculationParameters?.bonus?.availableBonuses.map(
          (x: any) =>
            ({
              id: x.id,
              value: x.description,
              bonusOriginNeeded: x.bonusOriginNeeded ?? "",
            } as AvailableBonus),
        ),
      } as Bonus,
      previousCompany: {
        ...state.bonus?.previousCompany,
        help: {
          title: json.uiContent.PreviousCompany.Popup.Header,
          description: json.uiContent.PreviousCompany.Popup.Body,
        },
        label: {
          title: json.uiContent.PreviousCompany.Header,
        },
        companyInput: {
          title: json.uiContent.CompanyInputHeader,
          state: 0,
          stateDescriptions: [undefined, json.uiContent.CompanyInputErrorMessage, undefined],
        },
        requiredErrorMessage: json.uiContent.CompanyErrorMessage,
        company: mapInsuranceCompanies(json.insuranceCompanies),
      },
      registrationNumber: {
        ...state.bonus?.registrationNumber,
        title: json.uiContent.BonusRegistrationNumberQuestionLabel,
        requiredErrorMessage: json.uiContent.BonusRegistrationNumberQuestionError,
        values: [
          {
            value: true,
            text: "Ja",
          },
          {
            value: false,
            text: "Nei",
          },
        ],
        regNumber: {
          title: json.uiContent.BonusRegistrationNumber.Header,
          state: 0,
          stateDescriptions: [undefined, json.uiContent.BonusRegistrationNumberRequired, json.uiContent.BonusRegistrationNumberIncorrect],
        },
      },
      date: {
        ...state.bonus?.date,
        title: json.uiContent.StartDate.Header,
        isDisabled: true,

        value: new Date(Date.parse(json.quoteValidFrom)),
      },
    },
    vehicle: {
      ...state.vehicle,
      regNoQuestion: {
        title: json.uiContent.VehicleRegNoQuestionTitle,
        selected: false,
        values: [
          {
            value: true,
            text: "Ja",
          },
          {
            value: false,
            text: "Nei",
          },
        ],
        requiredErrorMessage: json.uiContent.VehicleRegNoQuestionError,
        showError: false,
        help: {
          title: json.uiContent.VehicleRegNoQuestionHelpTitle,
          description: json.uiContent.VehicleRegNoQuestionHelpDescription,
        },
      },
      regNo: {
        ...state.vehicle?.regNo,
        title: json.uiContent.VehicleRegNoTitle,
        stateDescriptions: [undefined, json.uiContent.VehicleRegNoRequired, json.uiContent.VehicleRegNoIncorrect],
      },
      VINNo: {
        ...state.vehicle?.VINNo,
        title: json.uiContent.VehicleVINNoTitle,
        stateDescriptions: [undefined, json.uiContent.VehicleVINNoRequired, json.uiContent.VehicleVINNoIncorrect],
        description: {
          title: json.uiContent.VehicleVINNoHelpTitle,
          text: json.uiContent.VehicleVINNoHelpDescription,
        },
      },
    },
    inputs: {
      ...state.inputs,
      quoteId: json.quoteId,
      vehicle: { ...state.inputs.vehicle, ...json.vehicle },
    },
  } as ApplicationState;
};

export default quote;

export function preparePrice(price: any) {
  const newPrice = price.amount.toString();
  const currency = "kr/md.";

  return newPrice.concat(` ${currency}`);
}

export function mapCoverageItemsInCoverage(coverageItemsSource: any, includedCoverageItems: string[]) {
  let coverageItems = coverageItemsSource.map(
    (val: any) =>
      ({
        InternalCode: val.id,
        Name: val.name,
        Description: val.description,
      } as CoverageItem),
  );
  var filteredCoverageItems = coverageItems.filter((i: CoverageItem) => includedCoverageItems.includes(i.InternalCode));
  return filteredCoverageItems;
}

export function mapAddonsFromResponse(addOnsToMap: any, uiContent: any, partner: string) {
  const addOns = addOnsToMap?.map(
    (val: any) =>
      ({
        Description: addAddonDescriptionFromResponse(val, uiContent),
        Exclude: [],
        MonthlyPrice: preparePrice(val.price.find((x: any) => x.interval === "Monthly")),
        Name: val.name,
        PopupText: "This is popup text",
        SortOrder: 0,
        VariablePrice: "",
        InternalCode: val.id.toString(),
        visible: true,
        disabled: false,
        campaigned: false,
        Checked: val.isSelected,
        lowMillage: addLowMillageQuestionFromResponse(val, uiContent, partner),
      } as IAddOn),
  );
  return PrepareNorwayAddonTexts(addOns);
}

export function mapAddonsFromState(addOnsToMap: any, state: any) {
  const addOns = addOnsToMap?.map(
    (val: any) =>
      ({
        Description: addAddonDescriptionFromState(val, state),
        Exclude: [],
        MonthlyPrice: preparePrice(val.price.find((x: any) => x.interval === "Monthly")),
        Name: val.name,
        PopupText: "This is popup text",
        SortOrder: 0,
        VariablePrice: "",
        InternalCode: val.id.toString(),
        visible: true,
        disabled: false,
        campaigned: false,
        Checked: val.isSelected,
        lowMillage: addLowMillageQuestionFromState(val, state),
      } as IAddOn),
  );
  return PrepareNorwayAddonTexts(addOns);
}

export function addAddonDescriptionFromResponse(addon: any, uiContent: any) {
  if (addon.name.includes("Motor")) {
    return (
      uiContent?.AddonDescriptionMotor ??
      "Tilleggsforsikring som dekker skader på motor, drivverk og elektronikk inntil bilen har kjørt 200 000 km."
    );
  } else if (addon.name.includes("Leiebil")) {
    return (
      uiContent?.AddonDescriptionLeiebil ??
      "Med vår tilleggsforsikring for leiebil dekker vi leiebil så lenge bilen er på verksted, dersom skaden dekkes av forsikringen."
    );
  } else if (addon.name.includes("Maskinskade")) {
    return (
      uiContent?.AddonDescriptionMaskinskade ??
      "Hvis motor, girkasse eller drivverk havarerer innen bilen har kjørt 200 000 km, reparerer vi skaden eller bytter delen. For elbil og hybridbil dekker vi skader på batteripakke, inkludert tilhørende elektroniske styringsenheter. Kan kjøpes inntil bilen har kjørt 150 000 km."
    );
  } else if (addon.name.includes("Total Care")) {
    return (
      uiContent?.AddonDescriptionTotalCare ??
      "Med Total Care får du en utvidet trafikkulykkesforsikring, du får uhellsdekning uten bonustap, og du får helt ny bil hvis bilen din er under 3 år og ikke har passert 60 000 km. Total Care dekker også parkeringsskader uten bonustap (se vilkår)."
    );
  } else return "Description";
}

export function addAddonDescriptionFromState(addon: any, state: any) {
  if (addon.name.includes("Motor")) {
    return (
      state.texts?.addonDescriptionMotor ??
      "Tilleggsforsikring som dekker skader på motor, drivverk og elektronikk inntil bilen har kjørt 200 000 km."
    );
  } else if (addon.name.includes("Leiebil")) {
    return (
      state.texts?.addonDescriptionLeiebil ??
      "Med vår tilleggsforsikring for leiebil dekker vi leiebil så lenge bilen er på verksted, dersom skaden dekkes av forsikringen."
    );
  } else if (addon.name.includes("Maskinskade")) {
    return (
      state.texts?.addonDescriptionMaskinskade ??
      "Hvis motor, girkasse eller drivverk havarerer innen bilen har kjørt 200 000 km, reparerer vi skaden eller bytter delen. For elbil og hybridbil dekker vi skader på batteripakke, inkludert tilhørende elektroniske styringsenheter. Kan kjøpes inntil bilen har kjørt 150 000 km."
    );
  } else if (addon.name.includes("Total Care")) {
    return (
      state.texts?.addonDescriptionTotalCare ??
      "Med Total Care får du en utvidet trafikkulykkesforsikring, du får uhellsdekning uten bonustap, og du får helt ny bil hvis bilen din er under 3 år og ikke har passert 60 000 km. Total Care dekker også parkeringsskader uten bonustap (se vilkår)."
    );
  } else return "Description";
}

export function addCoverageDescriptionFromResponse(coverage: any, uiContent: any) {
  if (coverage.name.includes("Super")) {
    return uiContent?.CoverageDescriptionSuper ?? "";
  } else if (coverage.name.includes("Del")) {
    return uiContent?.CoverageDescriptionDelkasko ?? "";
  } else if (coverage.name.includes("Ansvar")) {
    return uiContent?.CoverageDescriptionAnsvar ?? "";
  } else if (coverage.name === "Kasko") {
    return uiContent?.CoverageDescriptionKasko ?? "";
  } else return "Description";
}

export function addCoverageDescriptionFromState(coverage: any, state: any) {
  if (coverage.name.includes("Super")) {
    return state.texts?.coverageDescriptions?.coverageDescriptionSuper ?? "";
  } else if (coverage.name.includes("Del")) {
    return state.texts?.coverageDescriptions?.coverageDescriptionDelkasko ?? "";
  } else if (coverage.name.includes("Ansvar")) {
    return state.texts?.coverageDescriptions?.coverageDescriptionAnsvar ?? "";
  } else if (coverage.name === "Kasko") {
    return state.texts?.coverageDescriptions?.coverageDescriptionKasko ?? "";
  } else return "Description";
}

export function addLowMillageQuestionFromResponse(addon: any, uiContent: any, partner: string) {
  const showQuestion =
    (partner === BrandsEnum.VolviaNo && addon?.name.includes("Maskinskade")) ||
    (partner === BrandsEnum.NissanNo && addon?.name.includes("Motor"));

  if (showQuestion) {
    return {
      questionVisible: true,
      selected: false,
      text:
        uiContent?.AddonMotorCheckboxDescription ??
        "Jeg bekrefter at min bil har gått under 150.00 km da dette er en forutsetning for a kunne kjøpe. Motor, gir og driftsbatteri",
      showError: false,
      requiredErrorMessage: uiContent?.AddonCheckboxErrorMessage ?? "error message!",
    } as LowMillage;
  } else {
    return {
      questionVisible: false,
      selected: false,
      text: "",
    } as LowMillage;
  }
}

export function addLowMillageQuestionFromState(addon: any, state: any) {
  const partner = state.inputs.partner;
  const showQuestion =
    (partner === BrandsEnum.VolviaNo && addon?.name.includes("Maskinskade")) ||
    (partner === BrandsEnum.NissanNo && addon?.name.includes("Motor"));

  if (showQuestion) {
    return {
      questionVisible: true,
      selected: false,
      text:
        state.texts?.addonMotorCheckboxDescription ??
        "Jeg bekrefter at min bil har gått under 150.00 km da dette er en forutsetning for a kunne kjøpe. Motor, gir og driftsbatteri",
      showError: false,
      requiredErrorMessage: state.texts?.addonCheckboxErrorMessage ?? "error message!",
    } as LowMillage;
  } else {
    return {
      questionVisible: false,
      selected: false,
      text: "",
    } as LowMillage;
  }
}

export function mapCoverageItems(coverageItemsToMap: any, coverages: Coverage[]) {
  let coverageItems = coverageItemsToMap.map(
    (val: any) =>
      ({
        InternalCode: val.id,
        Name: val.name,
        Description: val.description,
      } as CoverageItem),
  );
  const allowedItems: number[] = [];
  coverages.forEach((x: any) => allowedItems.push(...x.CoverageItems));
  const filteredCoverageItems = coverageItems.filter((x: CoverageItem) => allowedItems.find((y: any) => y.InternalCode === x.InternalCode));
  return filteredCoverageItems;
}

export function mapDeductibles(deductiblesToMap: any) {
  const deductibles = deductiblesToMap?.map(
    (val: any) =>
      ({
        Code: val.id,
        Name: val.value,
        IsSelected: val?.isSelected,
      } as Deductible),
  );
  return deductibles;
}

export function mapCoveragePrice(coveragePriceToMap: any) {
  const monthlyPrice = coveragePriceToMap?.find((x: any) => x.interval === "Monthly");
  return {
    Amount: monthlyPrice?.amount,
    Currency: "kr",
    Period: monthlyPrice?.interval,
    Discount: `${monthlyPrice?.amountDiscount} kr/md.`,
  } as CoveragePrice;
}

export function mapInsuranceCompanies(insuranceCompaniesToMap: any) {
  const comapnies = insuranceCompaniesToMap.map(
    (x: any) =>
      ({
        value: x.insuranceCompanyId,
        title: x.insuranceCompanyName,
      } as Company),
  );
  comapnies.sort((a: any, b: any) => a.title.localeCompare(b.title));
  comapnies.unshift({
    value: "0",
    title: "Velg selskap",
  } as Company);
  return comapnies;
}

export function mapFinancingCompanies(financingCompaniesToMap: any) {
  const financingCompanies = financingCompaniesToMap.map(
    (val: any) =>
      ({
        id: val.financingCompanyId,
        name: val.financingCompanyName,
        selected: false,
      } as IFinancingCompany),
  );

  return financingCompanies;
}

export function mapLeasingCompanies(leasingCompaniesToMap: any) {
  const leasingCompanies = leasingCompaniesToMap.map(
    (val: any) =>
      ({
        id: val.leasingCompanyId,
        name: val.leasingCompanyName,
        selected: false,
      } as IFinancingCompany),
  );

  return leasingCompanies;
}
